import { AttachmentPicker } from "./AttachmentPicker";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { api } from "../api/client";
import type {
  AuthSession,
  HomeStyleFieldWorkAllowedAction,
  HomeStyleFieldWorkProjectDetail,
  HomeStyleFieldWorkProjectSummary,
} from "../api/types";
import { errorMessage } from "../utils/errors";
import "./field-work.css";

interface FieldWorkAppProps {
  session: AuthSession;
  loggingOut: boolean;
  onLogout: () => Promise<void>;
}

const IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"]);
const MAX_IMAGE_BYTES = 25 * 1024 * 1024;

export function isHomeStyleFieldRole(role: AuthSession["role"]): role is "ELECTRICIAN" | "PHOTOGRAPHER" {
  return role === "ELECTRICIAN" || role === "PHOTOGRAPHER";
}

function roleLabel(role: "ELECTRICIAN" | "PHOTOGRAPHER") {
  return role === "ELECTRICIAN" ? "전기기사" : "사진기사";
}

function dateLabel(value: string | null) {
  return value ? value.replaceAll("-", ".") : "미정";
}

function statusLabel(value: HomeStyleFieldWorkProjectSummary["stage"]["status"]) {
  return ({ waiting: "예정", in_progress: "진행 중", completed: "완료", skipped: "해당 없음" } as const)[value];
}

function allows(detail: HomeStyleFieldWorkProjectDetail, action: HomeStyleFieldWorkAllowedAction) {
  return detail.stage.allowedActions.includes(action);
}

function todayInSeoul() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function AssignedFieldWorkApp({ session, loggingOut, onLogout, role }: FieldWorkAppProps & {
  role: "ELECTRICIAN" | "PHOTOGRAPHER";
}) {
  const [projects, setProjects] = useState<HomeStyleFieldWorkProjectSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string>();
  const [detail, setDetail] = useState<HomeStyleFieldWorkProjectDetail>();
  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [message, setMessage] = useState<string>();
  const detailControllerRef = useRef<AbortController>();

  const loadProjects = useCallback(async (signal?: AbortSignal, preferredId?: string) => {
    const page = await api.homeStyleFieldWorkProjects(signal);
    setProjects(page.items);
    setSelectedId((current) => {
      const candidate = preferredId ?? current;
      if (candidate && page.items.some((item) => item.id === candidate)) return candidate;
      return page.items[0]?.id;
    });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setListLoading(true);
    setFailure(undefined);
    void loadProjects(controller.signal).catch((error) => {
      if (!(error instanceof DOMException && error.name === "AbortError")) setFailure(errorMessage(error));
    }).finally(() => {
      if (!controller.signal.aborted) setListLoading(false);
    });
    return () => controller.abort();
  }, [loadProjects]);

  const loadDetail = useCallback(async (id: string) => {
    const controller = new AbortController();
    detailControllerRef.current?.abort();
    detailControllerRef.current = controller;
    setDetail(undefined);
    setDetailLoading(true);
    setFailure(undefined);
    setMessage(undefined);
    try {
      const next = await api.homeStyleFieldWorkDetail(id, controller.signal);
      if (!controller.signal.aborted) setDetail(next);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) setFailure(errorMessage(error));
    } finally {
      if (!controller.signal.aborted) setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(undefined);
      setDetailLoading(false);
      return;
    }
    void loadDetail(selectedId);
    return () => detailControllerRef.current?.abort();
  }, [loadDetail, selectedId]);

  const refresh = useCallback(async () => {
    if (!selectedId) return;
    await loadProjects(undefined, selectedId);
    await loadDetail(selectedId);
  }, [loadDetail, loadProjects, selectedId]);

  const run = async (successMessage: string, command: () => Promise<unknown>) => {
    setBusy(true);
    setFailure(undefined);
    setMessage(undefined);
    try {
      await command();
      setMessage(successMessage);
      await refresh();
    } catch (error) {
      setFailure(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const updateSchedule = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!detail) return;
    const scheduledOn = String(new FormData(event.currentTarget).get("scheduledOn") ?? "").trim() || null;
    void run("예정일을 저장했습니다.", () => api.updateHomeStyleFieldWorkSchedule(
      detail.id,
      detail.stage.code,
      { expectedVersion: detail.version, scheduledOn },
    ));
  };

  const completeStage = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!detail) return;
    const form = new FormData(event.currentTarget);
    const completedOn = String(form.get("completedOn") ?? "");
    const checklistKey = detail.stage.code === "electrical" ? "photoChecked" : "afterChecked";
    void run(`${detail.stage.label} 완료를 기록했습니다.`, () => api.completeHomeStyleFieldWorkStage(
      detail.id,
      detail.stage.code,
      {
        expectedVersion: detail.version,
        completedOn,
        checklist: { [checklistKey]: form.get("evidenceChecked") === "on" },
        startNext: false,
      },
    ));
  };

  const correctCompletion = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!detail) return;
    const form = new FormData(event.currentTarget);
    void run("완료일 정정을 저장했습니다.", () => api.correctHomeStyleFieldWorkCompletion(
      detail.id,
      detail.stage.code,
      {
        expectedVersion: detail.version,
        completedOn: String(form.get("correctedCompletedOn") ?? ""),
        reason: String(form.get("reason") ?? "").trim(),
      },
    ));
  };

  const uploadEvidence = (files: File[]) => {
    if (!detail || !files.length) return;
    if (files.length > 20) {
      setFailure("한 번에 최대 20장까지 선택할 수 있습니다.");
      return;
    }
    const invalid = files.find((file) => !IMAGE_TYPES.has(file.type) || file.size <= 0 || file.size > MAX_IMAGE_BYTES);
    if (invalid) {
      setFailure(`${invalid.name}: JPEG·PNG·WebP·HEIC, 25MB 이하 이미지만 업로드할 수 있습니다.`);
      return;
    }
    return run(`${files.length}장의 현장 사진을 업로드했습니다.`, () => api.uploadHomeStyleStageAssets(
      detail.id,
      detail.stage.code,
      files,
      detail.version,
    ));
  };

  const logout = () => {
    detailControllerRef.current?.abort();
    setDetail(undefined);
    setSelectedId(undefined);
    setMessage(undefined);
    setFailure(undefined);
    void onLogout();
  };

  const selectProject = (id: string) => {
    if (id === selectedId) return;
    detailControllerRef.current?.abort();
    setDetail(undefined);
    setDetailLoading(true);
    setMessage(undefined);
    setFailure(undefined);
    setSelectedId(id);
  };

  return (
    <div className="field-work-app project-selmo-design" data-field-role={role}>
      <header className="field-work-header">
        <a className="field-work-brand" href="/" aria-label="Project Selmo 현장 업무 홈">
          <span aria-hidden="true">ㅅ</span>
          <strong>PROJECT SELMO<small>{roleLabel(role)} 현장 업무</small></strong>
        </a>
        <div className="field-work-account">
          <span><strong>{session.name ?? roleLabel(role)}</strong><small>배정된 {role === "ELECTRICIAN" ? "전기 작업" : "사진 촬영"}만 표시됩니다</small></span>
          <button type="button" disabled={loggingOut} onClick={logout}>{loggingOut ? "로그아웃 중…" : "로그아웃"}</button>
        </div>
      </header>

      <main className="field-work-layout">
        <aside className="field-work-projects" aria-label="배정된 현장 목록">
          <header><div><small>MY SITES</small><h1>배정된 현장</h1></div><span>{projects.length}</span></header>
          {listLoading ? <p className="field-work-state" role="status">현장 목록을 불러오고 있습니다…</p>
            : projects.length ? <ul>{projects.map((project) => (
              <li key={project.id}>
                <button type="button" className={selectedId === project.id ? "is-selected" : ""} aria-current={selectedId === project.id ? "page" : undefined} onClick={() => selectProject(project.id)}>
                  <span><strong>{project.projectName}</strong><small>{[project.address, project.addressDetail].filter(Boolean).join(" · ") || "주소 확인 중"}</small></span>
                  <span className={`field-work-status is-${project.stage.status}`}>{statusLabel(project.stage.status)}</span>
                  <time>{dateLabel(project.stage.scheduledOn)}</time>
                </button>
              </li>
            ))}</ul> : <p className="field-work-state" role="status">현재 배정된 현장이 없습니다.</p>}
        </aside>

        <section className="field-work-detail" aria-live="polite">
          {failure ? <div className="field-work-alert" role="alert"><strong>업무를 처리하지 못했습니다.</strong><p>{failure}</p></div> : null}
          {message ? <div className="field-work-message" role="status">{message}</div> : null}
          {detailLoading ? <div className="field-work-empty" role="status">현장 상세를 안전하게 불러오고 있습니다…</div>
            : detail ? <>
              <header className="field-work-detail-header">
                <div><small>{detail.stage.label.toUpperCase()}</small><h2>{detail.projectName}</h2><p>{[detail.address, detail.addressDetail].filter(Boolean).join(" · ") || "주소 확인 중"}</p></div>
                <span className={`field-work-stage-chip is-${detail.stage.status}`}>{statusLabel(detail.stage.status)}</span>
              </header>

              <section className="field-work-private-card" aria-labelledby="field-work-site-info">
                <header><div><small>PRIVATE · NO STORE</small><h3 id="field-work-site-info">선택 현장 정보</h3></div></header>
                <dl>
                  <dt>고객</dt><dd>{detail.customer.displayName}</dd>
                  <dt>전화번호</dt><dd>{detail.customer.phone ? <a href={`tel:${detail.customer.phone.replace(/[^0-9+]/g, "")}`}>{detail.customer.phone}</a> : "미등록"}</dd>
                  <dt>출입 비밀번호</dt><dd>{detail.accessPassword ?? "미등록"}</dd>
                  <dt>주소</dt><dd>{detail.address ?? "미등록"} {detail.addressDetail ?? ""}</dd>
                </dl>
              </section>

              <section className="field-work-stage-card" aria-labelledby="field-work-stage-title">
                <header>
                  <div><small>MY ASSIGNED STAGE</small><h3 id="field-work-stage-title">{detail.stage.label}</h3></div>
                  <span>{detail.stage.scheduleMode === "MANUAL" ? "수동 일정" : "자동 일정"}</span>
                </header>
                <dl className="field-work-stage-summary">
                  <div><dt>예정일</dt><dd>{dateLabel(detail.stage.scheduledOn)}</dd></div>
                  <div><dt>완료일</dt><dd>{dateLabel(detail.stage.completedOn)}</dd></div>
                  <div><dt>자동 계산일</dt><dd>{dateLabel(detail.stage.autoOn)}</dd></div>
                  <div><dt>검증된 사진</dt><dd>{detail.stage.readyAssetCount}장</dd></div>
                </dl>
                {detail.stage.scheduleWarning ? <p className="field-work-warning" role="status">{detail.stage.scheduleWarning}</p> : null}

                <div className="field-work-actions">
                  {allows(detail, "edit_schedule") ? <form onSubmit={updateSchedule}>
                    <label><span>예정일 직접 수정</span><input type="date" name="scheduledOn" defaultValue={detail.stage.scheduledOn ?? ""} /></label>
                    <button type="submit" disabled={busy}>예정일 저장</button>
                  </form> : null}

                  {allows(detail, "restore_auto") ? <section className="field-work-inline-action">
                    <div><strong>자동 계산으로 되돌리기</strong><small>이 프로젝트에 저장된 일정 규칙으로 다시 계산합니다.</small></div>
                    <button type="button" disabled={busy} onClick={() => void run("자동 계산 일정으로 복원했습니다.", () => api.restoreHomeStyleFieldWorkSchedule(detail.id, detail.stage.code, detail.version))}>자동복원</button>
                  </section> : null}

                  {allows(detail, "upload_evidence") ? <section className="field-work-inline-action">
                    <div><strong>현장 사진 증거</strong><small>본인 단계 사진만 업로드할 수 있습니다.</small></div>
                    <AttachmentPicker key={`${detail.id}-${detail.stage.code}`} label="사진 선택" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" maxBytes={MAX_IMAGE_BYTES} maxFiles={20} disabled={busy} busy={busy} onFiles={uploadEvidence} help="선택한 사진은 바로 업로드됩니다." />
                  </section> : null}

                  {allows(detail, "complete") ? <form onSubmit={completeStage}>
                    <label><span>완료일</span><input required type="date" name="completedOn" defaultValue={todayInSeoul()} /></label>
                    <label className="field-work-check"><input required type="checkbox" name="evidenceChecked" /><span>{detail.stage.code === "electrical" ? "전기 작업 사진을 확인했습니다." : "최종 촬영 결과를 확인했습니다."}</span></label>
                    <button type="submit" disabled={busy || detail.stage.readyAssetCount < 1}>완료 기록</button>
                  </form> : null}

                  {allows(detail, "correct_completion") ? <form onSubmit={correctCompletion}>
                    <label><span>완료일 정정</span><input required type="date" name="correctedCompletedOn" defaultValue={detail.stage.completedOn ?? ""} /></label>
                    <label><span>정정 사유</span><textarea required minLength={2} name="reason" rows={3} /></label>
                    <button type="submit" disabled={busy}>사유와 함께 정정</button>
                  </form> : null}
                </div>
              </section>
            </> : <div className="field-work-empty" role="status">왼쪽에서 배정 현장을 선택해 주세요.</div>}
        </section>
      </main>
    </div>
  );
}

export function FieldWorkApp(props: FieldWorkAppProps) {
  if (!isHomeStyleFieldRole(props.session.role)) {
    return (
      <main className="field-work-app field-work-denied project-selmo-design" role="alert">
        <strong>현장 업무 권한이 없습니다.</strong>
        <p>전기기사 또는 사진기사로 배정된 활성 계정만 이 화면을 사용할 수 있습니다.</p>
      </main>
    );
  }
  return <AssignedFieldWorkApp {...props} role={props.session.role} />;
}
